// ===========================================
// Consent Modal
// Non-dismissible 3-step dialog for first-login T&C and media exposure consent
// Steps: T&Cs → Privacy Policy → Media Exposure (a second contact: T&Cs → Privacy only, #442)
// A step's required tick box lives in the footer so it is never below the fold on a
// short phone screen (#441); phones get a full-screen dialog with ONE scroll area.
// ===========================================

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import MediaExposureSelector from './MediaExposureSelector';
import { useAcknowledgeConsent, useDeclineConsent } from '../../hooks/useConsent';
import type { MediaExposure } from '../../api/consent.api';

// ===========================================
// TYPES
// ===========================================

interface ConsentModalProps {
  open: boolean;
  familyId: string;
  /**
   * #442: PRIMARY agrees for the family and picks its media choice (3 steps). CONTACT (a second
   * contact) agrees for themselves only: T&Cs and Privacy, no media step (D1).
   */
  kind: 'PRIMARY' | 'CONTACT';
  /** The policy text is still loading: show a spinner in its place. */
  contentLoading?: boolean;
  termsContent?: string | null;
  privacyContent?: string | null;
  schoolName: string;
  /** The school's logo, shown at the top so the screen carries the school's branding */
  logoUrl?: string;
  onConsented: () => void;
  /** `declined` is false when the decline request failed (the parent is logged out either way). */
  onDeclined: (declined: boolean) => void;
}

const PRIMARY_STEPS = ['Terms & Conditions', 'Privacy Policy', 'Media Consent'];
const CONTACT_STEPS = ['Terms & Conditions', 'Privacy Policy'];

// ===========================================
// COMPONENT
// ===========================================

export default function ConsentModal({
  open,
  familyId,
  kind,
  contentLoading = false,
  termsContent,
  privacyContent,
  schoolName,
  logoUrl,
  onConsented,
  onDeclined,
}: ConsentModalProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [mediaExposure, setMediaExposure] = useState<Exclude<MediaExposure, 'PENDING'> | null>(null);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const acknowledge = useAcknowledgeConsent();
  const decline = useDeclineConsent();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm')); // full screen on a phone (#441)

  const isContact = kind === 'CONTACT';
  const steps = isContact ? CONTACT_STEPS : PRIMARY_STEPS;
  const lastStep = steps.length - 1;

  // Steps 0 and 1 each need their tick box before Next (or a contact's Accept) is enabled.
  const stepChecked = activeStep === 0 ? termsChecked : privacyChecked;

  const handleNext = () => {
    if (!stepChecked) return;
    setSubmitted(false);
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => {
    setSubmitted(false);
    setActiveStep((s) => s - 1);
  };

  const handleComplete = async () => {
    setSubmitted(true);
    if (isContact ? !(termsChecked && privacyChecked) : !mediaExposure) return;
    setSubmitError(null);
    try {
      await acknowledge.mutateAsync({
        familyId,
        // A second contact never sends the family's media choice (#442 D1).
        payload: isContact
          ? { termsAccepted: true, privacyAccepted: true }
          : { termsAccepted: true, privacyAccepted: true, mediaExposure: mediaExposure! },
      });
      onConsented();
    } catch {
      setSubmitError('Something went wrong saving your preferences. Please try again.');
    }
  };

  const handleConfirmDecline = async () => {
    let declined = false;
    try {
      await decline.mutateAsync(familyId);
      declined = true;
    } catch {
      // token may already be revoked — proceed, but don't claim the school was told (#442 /qa L5)
    } finally {
      onDeclined(declined);
    }
  };

  return (
    <>
      {/* Main consent dialog — non-dismissible */}
      <Dialog
        open={open && !showDeclineConfirm}
        maxWidth="md"
        fullWidth
        fullScreen={isPhone}
        disableEscapeKeyDown
        PaperProps={{ sx: { borderRadius: isPhone ? 0 : 2 } }}
      >
        <DialogTitle sx={{ pb: 1, textAlign: 'center' }}>
          {logoUrl && (
            <Box
              component="img"
              src={logoUrl}
              alt={schoolName}
              sx={{ display: 'block', mx: 'auto', mb: 1, maxHeight: { xs: 56, sm: 72 }, maxWidth: 160 }}
            />
          )}
          <Typography
            variant="h6"
            component="h2"
            fontWeight={700}
            sx={{ fontFamily: theme.typography.h4.fontFamily }}
          >
            Welcome to {schoolName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Before you continue, please read and agree to the following.
          </Typography>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: isPhone ? 2 : 3 }}>
          {isPhone ? (
            // Three wrapped step labels took a fifth of a phone screen (#441)
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
              Step {activeStep + 1} of {steps.length}
            </Typography>
          ) : (
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          {contentLoading && activeStep < 2 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} aria-label="Loading the policy" />
            </Box>
          )}

          {/* Step 0 — Terms & Conditions */}
          {activeStep === 0 && !contentLoading && (
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                Terms &amp; Conditions
              </Typography>
              {termsContent ? (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    // On a phone the dialog body is the one scroll area (#441)
                    maxHeight: isPhone ? 'none' : 320,
                    overflowY: isPhone ? 'visible' : 'auto',
                    bgcolor: 'background.paper',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {termsContent}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Please contact the school for a copy of their Terms &amp; Conditions.
                </Typography>
              )}
            </Stack>
          )}

          {/* Step 1 — Privacy Policy */}
          {activeStep === 1 && !contentLoading && (
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                Privacy Policy
              </Typography>
              {privacyContent ? (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    // On a phone the dialog body is the one scroll area (#441)
                    maxHeight: isPhone ? 'none' : 320,
                    overflowY: isPhone ? 'visible' : 'auto',
                    bgcolor: 'background.paper',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {privacyContent}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Please contact the school for a copy of their Privacy Policy.
                </Typography>
              )}
              {isContact && (
                <Typography variant="body2" color="text.secondary">
                  Your family&apos;s photo and media choice is set by your family&apos;s primary
                  contact.
                </Typography>
              )}
            </Stack>
          )}

          {/* Step 2 — Media Exposure */}
          {activeStep === 2 && !isContact && (
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                Media &amp; Marketing Consent
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please select how you would like your child&apos;s image to be used by{' '}
                {schoolName}. You can change this at any time from your account settings.
              </Typography>
              <MediaExposureSelector
                value={mediaExposure}
                onChange={setMediaExposure}
              />
              {submitted && !mediaExposure && (
                <Typography variant="caption" color="error">
                  Please select a media consent option to continue.
                </Typography>
              )}
            </Stack>
          )}

          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
        </DialogContent>

        <Divider />

        {/* The step's required tick box, pinned beside Next so it is always on screen (#441) */}
        {activeStep < 2 && (
          <Box sx={{ px: 3, pt: 1.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={stepChecked}
                  // Agree to what has loaded, never to a spinner (#442 /qa L9)
                  disabled={contentLoading}
                  onChange={(e) =>
                    activeStep === 0
                      ? setTermsChecked(e.target.checked)
                      : setPrivacyChecked(e.target.checked)
                  }
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  {activeStep === 0
                    ? <>I have read and agree to the Terms &amp; Conditions.</>
                    : <>I have read and agree to the Privacy Policy.</>}{' '}
                  <strong>(Required)</strong>
                </Typography>
              }
            />
          </Box>
        )}

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            justifyContent: 'space-between',
            // Clear the iPhone home indicator when full screen
            pb: isPhone ? 'max(16px, env(safe-area-inset-bottom))' : 2,
          }}
        >
          {/* Decline button — always visible */}
          <Button
            variant="text"
            color="error"
            size="small"
            onClick={() => setShowDeclineConfirm(true)}
            disabled={acknowledge.isPending || decline.isPending}
          >
            I Do Not Accept
          </Button>

          <Stack direction="row" spacing={1}>
            {activeStep > 0 && (
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={acknowledge.isPending}
              >
                Back
              </Button>
            )}
            {activeStep < lastStep ? (
              <Button variant="contained" onClick={handleNext} disabled={!stepChecked}>
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleComplete}
                disabled={
                  acknowledge.isPending ||
                  (isContact ? !(termsChecked && privacyChecked) : !mediaExposure)
                }
                startIcon={acknowledge.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {acknowledge.isPending ? 'Saving…' : 'Accept & Continue'}
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Decline confirmation dialog */}
      <Dialog
        open={showDeclineConfirm}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          <Typography variant="h6" fontWeight={700}>
            Are you sure?
          </Typography>
        </DialogTitle>
        <DialogContent>
          {isContact ? (
            // A second contact's decline never touches the family (#442 D1a).
            <Typography variant="body1">
              By declining, you will be <strong>logged out</strong> and the school will be told.
              Your family&apos;s enrolment is not affected. You can log in again and accept at any
              time.
            </Typography>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 1 }}>
                By declining, you will be <strong>logged out</strong> and your school
                administrator will be notified.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The process to unenrol your students from {schoolName} may begin. If this was a
                mistake, please contact the school directly.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setShowDeclineConfirm(false)}
            disabled={decline.isPending}
          >
            Go Back
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDecline}
            disabled={decline.isPending}
            startIcon={decline.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {decline.isPending ? 'Processing…' : 'Decline & Log Out'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
